import { validateAnalysisState } from '@aibi/analysis-engine';
import type { SemanticModel } from '@aibi/contracts';
import { SemanticModelIndex } from '@aibi/semantic-model';
import { ok, type AppError, type Result } from '@aibi/shared';
import { buildFieldCatalog } from '../model/field-catalog';
import type { WorkspaceDocument } from '../model/workspace-document';
import type { SemanticModelCatalog } from './semantic-model-catalog';
import { createStarterWorkspace, workspaceIdFor } from './starter-workspace';
import type { WorkspaceRepository } from './workspace-repository';

export interface OpenedWorkspace {
  readonly model: SemanticModel;
  readonly document: WorkspaceDocument;
  /** `true` si se recupero un borrador guardado; `false` si se creo el analisis inicial. */
  readonly restored: boolean;
}

/**
 * Abre el analisis de un modelo: carga y valida el modelo, intenta recuperar el borrador
 * y lo descarta si ya no es coherente con el modelo (campos eliminados, otro modelo...).
 *
 * @example
 * const opened = await openWorkspace({ modelCatalog, workspaceRepository }, 'demo.retail');
 */
export async function openWorkspace(
  services: {
    readonly modelCatalog: SemanticModelCatalog;
    readonly workspaceRepository: WorkspaceRepository;
  },
  modelId: string,
): Promise<Result<OpenedWorkspace, AppError>> {
  const loaded = await services.modelCatalog.load(modelId);
  if (!loaded.ok) return loaded;
  const model = loaded.value;
  const index = new SemanticModelIndex(model);

  const draft = await services.workspaceRepository.load(workspaceIdFor(model.id));
  if (draft.ok && draft.value !== null && isCoherent(draft.value, model.id, index)) {
    return ok({ model, document: draft.value, restored: true });
  }
  const catalog = buildFieldCatalog(index);
  return ok({
    model,
    document: createStarterWorkspace(model.id, model.name, { index, catalog }),
    restored: false,
  });
}

function isCoherent(
  document: WorkspaceDocument,
  modelId: string,
  index: SemanticModelIndex,
): boolean {
  return (
    document.semanticModelId === modelId &&
    document.cards.every((card) => validateAnalysisState(card, index).ok)
  );
}
